import React, { useState, useEffect } from "react";

/**
 * Loading animation with cold-start warning for Render free tier
 * Shows a progress animation and helpful message about server wake-up time
 */
export default function ColdStartLoader({ message = "Verbindung wird hergestellt..." }) {
  const [progress, setProgress] = useState(0);
  const [showExtendedMessage, setShowExtendedMessage] = useState(false);

  useEffect(() => {
    // Simulate loading progress (fake but gives user feedback)
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        // Slow down progress as it gets higher to simulate real loading
        if (prev < 30) return prev + 2;
        if (prev < 60) return prev + 1;
        if (prev < 85) return prev + 0.5;
        if (prev < 95) return prev + 0.2;
        return prev;
      });
    }, 100);

    // Show extended message after 5 seconds
    const extendedTimeout = setTimeout(() => {
      setShowExtendedMessage(true);
    }, 5000);

    return () => {
      clearInterval(progressInterval);
      clearTimeout(extendedTimeout);
    };
  }, []);

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full mx-4 text-center">
        {/* Animated Logo/Spinner */}
        <div className="mb-6">
          <div className="relative w-20 h-20 mx-auto">
            {/* Outer spinning ring */}
            <div className="absolute inset-0 border-4 border-emerald-200 rounded-full animate-pulse"></div>
            <div className="absolute inset-0 border-4 border-transparent border-t-emerald-500 rounded-full animate-spin"></div>
            
            {/* Inner pulsing dot */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full animate-pulse"></div>
            </div>
          </div>
        </div>

        {/* Main message */}
        <h3 className="text-lg font-semibold text-slate-800 mb-2">
          {message}
        </h3>

        {/* Progress bar */}
        <div className="w-full bg-slate-200 rounded-full h-2 mb-4 overflow-hidden">
          <div 
            className="bg-gradient-to-r from-emerald-500 to-teal-500 h-full rounded-full transition-all duration-300 ease-out"
            style={{ width: `${progress}%` }}
          ></div>
        </div>

        {/* Extended message after 5 seconds */}
        <div className={`transition-all duration-500 ${showExtendedMessage ? 'opacity-100 max-h-40' : 'opacity-0 max-h-0 overflow-hidden'}`}>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <svg className="w-5 h-5 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-amber-800">
                  Server wird gestartet
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  Der kostenlose Server schläft nach Inaktivität ein. 
                  Der erste Start kann bis zu 1-2 Minuten dauern. 
                  Bitte haben Sie etwas Geduld.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Animated dots */}
        <div className="flex justify-center gap-1 mt-4">
          <div className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
          <div className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
          <div className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
        </div>
      </div>
    </div>
  );
}
